import { request, requestNoContent } from './client';
import type { PagedResponse } from './unitsOfMeasure';

/** Chủ thể của một quy tắc phạm vi dữ liệu. */
export const AuthorizationSubjectType = {
  /** Quy tắc áp cho một vai trò. Đây là cách dùng thường xuyên. */
  Role: 1,

  /** Quy tắc áp cho một người dùng cụ thể. Chỉ dùng cho ngoại lệ. */
  User: 2,
} as const;

/**
 * Loại phạm vi dữ liệu.
 *
 * Giá trị phải khớp đúng kiểu liệt kê phía máy chủ. Chỉ hai giá trị đầu và giá trị khách hàng được
 * phân công là đã cài đặt thật; những giá trị còn lại đã khai trong thiết kế nhưng chưa có module sở
 * hữu dữ liệu của chúng — xem `implementedScopeTypes` bên dưới.
 */
export const DataScopeType = {
  /** Mọi pháp nhân. Dành cho quản trị hệ thống. */
  AllCompanies: 1,

  /** Toàn bộ pháp nhân đang làm việc. */
  CurrentCompany: 2,

  /** Địa điểm của người dùng. */
  Branch: 3,

  /** Chỉ phòng ban của người dùng. */
  OwnDepartment: 4,

  /** Phòng ban của người dùng và toàn bộ phòng ban cấp dưới. */
  OwnDepartmentAndBelow: 5,

  /** Chỉ dự án mà người dùng là thành viên. */
  ProjectMember: 6,

  /** Chỉ dự án mà người dùng làm quản lý dự án. */
  ManagedProjects: 7,

  /** Chỉ khách hàng mà người dùng phụ trách. */
  AssignedCustomers: 8,

  /** Chỉ bản ghi do chính người dùng tạo. */
  OwnRecordsOnly: 9,

  /** Bản ghi do chính người dùng lập, hoặc có tên người dùng trong danh sách được giao. */
  OwnOrAssignedRecords: 10,
} as const;

/** Thông tin một vai trò kèm số quyền, số người giữ và số quy tắc phạm vi. */
export interface RoleSummary {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly isSystemRole: boolean;
  readonly isAssignable: boolean;
  readonly isActive: boolean;
  readonly sortOrder: number;
  readonly maximumApprovalAmount: number | null;
  readonly permissionCount: number;
  readonly assignedUserCount: number;

  /**
   * Số quy tắc phạm vi dữ liệu.
   *
   * Bằng không nghĩa là người giữ vai trò này **không thấy dòng dữ liệu nào** — họ mở màn hình ra và
   * thấy danh sách rỗng. Đây là cấu hình sai dễ mắc nhất khi lập vai trò mới.
   */
  readonly dataScopeRuleCount: number;
}

/** Một quyền đang được vai trò giữ. */
export interface GrantedPermission {
  readonly permissionId: string;
  readonly code: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly isSensitive: boolean;
  readonly requiresTwoFactor: boolean;
  readonly grantedAt: string;
  readonly grantReason: string | null;
}

/** Một quy tắc phạm vi dữ liệu. */
export interface DataScopeRule {
  readonly id: string;
  readonly subjectType: number;
  readonly subjectId: string;
  readonly subjectDisplayName: string;
  readonly resourceName: string;
  readonly scopeType: number;
  readonly specificDepartmentId: string | null;
  readonly priority: number;
  readonly isUnrestrictedWithinCompany: boolean;
}

/** Thông tin đầy đủ của một vai trò. */
export interface RoleDetail {
  readonly role: RoleSummary;
  readonly permissions: readonly GrantedPermission[];
  readonly dataScopeRules: readonly DataScopeRule[];
}

/** Một quyền trong danh mục, đã tách sẵn mã module và tên tài nguyên. */
export interface PermissionCatalogEntry {
  readonly id: string;
  readonly code: string;
  readonly displayName: string;
  readonly description: string | null;
  readonly isSensitive: boolean;
  readonly requiresTwoFactor: boolean;
  readonly isObsolete: boolean;
  readonly moduleCode: string;
  readonly resourceName: string;
}

/** Điều kiện lọc danh sách vai trò. */
export interface RoleFilter {
  readonly searchTerm?: string;
  readonly includeInactive?: boolean;
  readonly pageNumber?: number;
  readonly pageSize?: number;
}

/** Nội dung gửi lên khi lập vai trò mới. */
export interface CreateRolePayload {
  readonly code: string;
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
}

/** Nội dung gửi lên khi sửa vai trò. */
export interface UpdateRolePayload {
  readonly name: string;
  readonly nameEn: string | null;
  readonly description: string | null;
  readonly sortOrder: number;
  readonly isAssignable: boolean;
  readonly maximumApprovalAmount: number | null;
}

/** Nội dung gửi lên khi lập quy tắc phạm vi dữ liệu. */
export interface CreateDataScopeRulePayload {
  readonly subjectType: number;
  readonly subjectId: string;
  readonly resourceName: string;
  readonly scopeType: number;
  readonly specificDepartmentId: string | null;
  readonly priority: number;
}

const roleRoute = '/api/v1/platform/roles';
const dataScopeRoute = '/api/v1/platform/data-scope-rules';

/** Dựng phần truy vấn của địa chỉ, bỏ hẳn tham số rỗng. */
function buildQueryString(filter: RoleFilter): string {
  const parameters = new URLSearchParams();

  if (filter.searchTerm !== undefined && filter.searchTerm.trim() !== '') {
    parameters.set('searchTerm', filter.searchTerm.trim());
  }

  if (filter.includeInactive === true) {
    parameters.set('includeInactive', 'true');
  }

  if (filter.pageNumber !== undefined) {
    parameters.set('pageNumber', String(filter.pageNumber));
  }

  if (filter.pageSize !== undefined) {
    parameters.set('pageSize', String(filter.pageSize));
  }

  const query = parameters.toString();

  return query === '' ? '' : `?${query}`;
}

/** Lấy một trang danh sách vai trò. */
export async function listRoles(filter: RoleFilter): Promise<PagedResponse<RoleSummary>> {
  return await request<PagedResponse<RoleSummary>>(`${roleRoute}${buildQueryString(filter)}`);
}

/** Lấy chi tiết một vai trò kèm quyền và quy tắc phạm vi. */
export async function getRole(id: string): Promise<RoleDetail> {
  return await request<RoleDetail>(`${roleRoute}/${id}`);
}

/** Lấy toàn bộ danh mục quyền. */
export async function listPermissionCatalog(): Promise<readonly PermissionCatalogEntry[]> {
  return await request<readonly PermissionCatalogEntry[]>(`${roleRoute}/permission-catalog`);
}

/** Lập một vai trò mới. */
export async function createRole(payload: CreateRolePayload): Promise<RoleDetail> {
  return await request<RoleDetail>(roleRoute, { method: 'POST', body: payload });
}

/** Sửa một vai trò. */
export async function updateRole(
  id: string,
  payload: UpdateRolePayload,
): Promise<RoleDetail> {
  return await request<RoleDetail>(`${roleRoute}/${id}`, { method: 'PUT', body: payload });
}

/** Ngừng dùng hoặc dùng lại một vai trò. */
export async function setRoleActive(id: string, isActive: boolean): Promise<RoleDetail> {
  return await request<RoleDetail>(`${roleRoute}/${id}/active`, {
    method: 'PUT',
    body: { isActive },
  });
}

/** Cấp một quyền cho vai trò. */
export async function grantPermissionToRole(
  id: string,
  permissionId: string,
  grantReason: string | null,
): Promise<RoleDetail> {
  return await request<RoleDetail>(`${roleRoute}/${id}/permissions`, {
    method: 'POST',
    body: { permissionId, grantReason },
  });
}

/** Thu hồi một quyền khỏi vai trò. */
export async function revokePermissionFromRole(
  id: string,
  permissionId: string,
): Promise<RoleDetail> {
  return await request<RoleDetail>(`${roleRoute}/${id}/permissions/${permissionId}`, {
    method: 'DELETE',
  });
}

/** Lập một quy tắc phạm vi dữ liệu. */
export async function createDataScopeRule(
  payload: CreateDataScopeRulePayload,
): Promise<DataScopeRule> {
  return await request<DataScopeRule>(dataScopeRoute, { method: 'POST', body: payload });
}

/** Xóa một quy tắc phạm vi dữ liệu. */
export async function deleteDataScopeRule(ruleId: string): Promise<void> {
  await requestNoContent(`${dataScopeRoute}/${ruleId}`, { method: 'DELETE' });
}
